import { Avatar, Box, Button, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { FC, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth, useDialog } from '@/logic/state';

import { DateTime } from 'luxon';

import { UserModel } from '@/features/login-and-signup/types/user-model';
import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useRouter } from 'next/router';
import ImageBoxFuture from '../shared/image-box-future';

const AvatarImg = '/icons/side-menu/avatar.svg';

interface ProfileCardProps {
  user: UserModel | undefined;
  close: () => void;
}
export const ProfileCard: FC<ProfileCardProps> = ({ user, close }) => {
  const theme = useTheme();
  const _user = useAuth(s => s.user, shallow);
  const setDialog = useDialog(s => s.setDialog, shallow);
  const intl = useIntl();

  const handleEditClick = useCallback(() => {
    if (_user.isCompleteProfile) {
      close();
    } else {
      setDialog({
        wrapper: true,
        isCompleteProfileForm: true,
      });
    }
  }, [_user?.isCompleteProfile, close, setDialog]);

  const { locale } = useRouter();

  return (
    user && (
      <Box>
        <Avatar
          sx={{
            marginX: 'auto',
            width: pxToRem(190),
            height: pxToRem(190),
          }}
        >
          <ImageBoxFuture
            img={_user?.photoUrl || AvatarImg}
            title={intl.formatMessage({
              id: _user?.photoUrl ? 'alt.user_image' : 'alt.avatar',
            })}
            objectFit='cover'
            sx={{
              width: '100%',
              height: '100%',
            }}
          />
        </Avatar>
        <Stack sx={{ margin: 'auto', marginTop: pxToRem(24), textAlign: 'center' }}>
          <Typography
            component='div'
            sx={{
              fontSize: pxToRem(24),
              fontWeight: '700',
              color: '#000000',
              overflowWrap: 'anywhere',
            }}
          >
            {user?.firstName} {user?.lastName}
          </Typography>
          <Typography
            component='div'
            sx={{
              fontSize: pxToRem(18),
              fontWeight: '400',
              lineHeight: pxToRem(27),
              color: theme.palette.grey[50],
              marginTop: pxToRem(8),
            }}
          >
            <FormattedMessage id='profile.member.since' /> &nbsp;
            {DateTime.fromISO(user.createdAt, {
              locale,
            }).toFormat('dd MMM yyyy')}
          </Typography>
        </Stack>
        <Stack>
          {user.dateOfBirth && (
            <Stack direction='row' spacing='auto' sx={{ marginTop: pxToRem(22) }}>
              <Typography
                component='div'
                sx={{
                  fontSize: pxToRem(18),
                  fontWeight: '400',
                  lineHeight: pxToRem(27),
                  color: theme.palette.grey[50],
                }}
              >
                <FormattedMessage id='global.birthday' />
              </Typography>
              <Typography
                component='div'
                sx={{
                  fontSize: pxToRem(18),
                  fontWeight: '400',
                  lineHeight: pxToRem(27),
                  color: theme.palette.grey[80],
                  marginTop: pxToRem(16),
                }}
              >
                {DateTime.fromISO(user.dateOfBirth, { locale }).toFormat('dd/MMM/yyyy')}
              </Typography>
            </Stack>
          )}
          {user.gender && (
            <Stack direction='row' spacing='auto' sx={{ marginTop: pxToRem(16) }}>
              <Typography
                component='div'
                sx={{
                  fontSize: pxToRem(18),
                  fontWeight: '400',
                  lineHeight: pxToRem(27),
                  color: theme.palette.grey[50],
                }}
              >
                <FormattedMessage id='global.gender' />
              </Typography>
              <Typography
                sx={{
                  fontSize: pxToRem(18),
                  fontWeight: '400',
                  lineHeight: pxToRem(27),
                  color: theme.palette.grey[80],
                  marginTop: pxToRem(16),
                }}
              >
                <FormattedMessage
                  id={`global.gender.${user.gender.toLocaleLowerCase()}`}
                />
              </Typography>
            </Stack>
          )}
          {user.email && (
            <Stack direction='row' spacing='auto' sx={{ marginTop: pxToRem(16) }}>
              <Typography
                component='div'
                sx={{
                  fontSize: pxToRem(18),
                  fontWeight: '400',
                  lineHeight: pxToRem(27),
                  color: theme.palette.grey[50],
                }}
              >
                <FormattedMessage id='global.email' />
              </Typography>
              <Tooltip title={user.email}>
                <Typography
                  sx={{
                    fontSize: pxToRem(18),
                    fontWeight: '400',
                    lineHeight: pxToRem(27),
                    color: theme.palette.grey[80],
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    width: '50%',
                    whiteSpace: 'nowrap',
                    textAlign: 'end',
                  }}
                >
                  {user.email}
                </Typography>
              </Tooltip>
            </Stack>
          )}

          <Stack direction='row' spacing='auto' sx={{ marginTop: pxToRem(16) }}>
            <Typography
              component='div'
              sx={{
                fontSize: pxToRem(18),
                fontWeight: '400',
                lineHeight: pxToRem(27),
                color: theme.palette.grey[50],
              }}
            >
              <FormattedMessage id='global.phone_number' />
            </Typography>
            <Typography
              component='div'
              sx={{
                fontSize: pxToRem(18),
                fontWeight: '400',
                lineHeight: pxToRem(27),
                color: theme.palette.grey[80],
                marginTop: pxToRem(16),
                unicodeBidi: 'plaintext',
              }}
            >
              {user.phoneNumber}
            </Typography>
          </Stack>
        </Stack>
        <Button
          id={'profile-edit'}
          variant='secondary'
          sx={{
            display: 'block',
            margin: 'auto',
            marginTop: pxToRem(32),
            padding: `${pxToRem(10)} ${pxToRem(28)}`,
          }}
          onClick={handleEditClick}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <ImageBoxFuture
              img='/icons/shared/edit-profile-logo.svg'
              title={intl.formatMessage({
                id: 'alt.edit_icon',
              })}
              objectFit='contain'
              sx={{
                width: '40px',
                height: '40px',
              }}
            />
            <Typography
              component='div'
              sx={{
                fontSize: pxToRem(16),
                fontWeight: '700',
                lineHeight: pxToRem(27),
              }}
            >
              <FormattedMessage id='profile.edit' />
            </Typography>
          </Box>
        </Button>
      </Box>
    )
  );
};
